import { Cloudinary } from '@cloudinary/url-gen';
import { limitFit } from '@cloudinary/url-gen/actions/resize';
import { maybe, Maybe } from '@passionware/monads';
import { makeFileURL } from 'lib/s3/s3Helper';

type ImageQualityOptions = {
  height?: number | 'auto';
  width?: number | 'auto';
  quality?: number | 'auto';
  format?: string;
};

export const IMAGE_QUALITY_OPTIONS = {
  small: { height: 400 * window.devicePixelRatio, quality: 80 },
  medium: {
    height: 400 * window.devicePixelRatio,
    width: 'auto',
    quality: 'auto'
  },
  large: { width: 2000 * window.devicePixelRatio, quality: 'auto' },
  full: { quality: 'auto' }
} satisfies Record<string, ImageQualityOptions>;

export const getImageSettingsQuery = ({ width, height, quality, format }) => {
  let query = `?quality=${quality}`;
  if (width) query += `&width=${width}`;
  if (height) query += `&height=${height}`;
  if (format) query += `&format=${format}`;
  return query;
};

/**
 * Returns an image URL with image transforms to perform from the file
 * @param {{id: int, url: string}} file File object
 * @param {{width: number, height: number, quality: number, format: string}} settings
 * @return {string}
 */
export const getResizedImageFileURL = (
  file,
  settings = { ...IMAGE_QUALITY_OPTIONS.medium, format: 'jpeg' }
) => {
  const fileURL = makeFileURL(file);

  return `${fileURL}${getImageSettingsQuery(settings)}`;
};

/**
 * Returns an image URL with image transforms to perform
 * @param {String} fileURL File URL
 * @param {{width: number, height: number, quality: number, format: string}} settings Image settings
 * @return {string}
 */
export const getResizedImageURL = (
  fileURL: string,
  settings = { ...IMAGE_QUALITY_OPTIONS.medium, format: 'jpeg' }
) => {
  return `${fileURL}${getImageSettingsQuery(settings)}`;
};

const isLocalhost = (() => {
  const result =
    window.location.hostname === 'localhost' ||
    window.location.hostname === '127.0.0.1';
  return () => result;
})();

const localhostPrefixes = [
  'http://localhost:',
  'http://127.0.0.1:',
  'http://0.0.0.0:',
  'https://localhost:',
  'https://127.0.0.1:',
  'https://0.0.0.0:'
];

function hasHttpUrlScheme(url: string): boolean {
  return url.startsWith('https://') || url.startsWith('http://');
}

function isPubliclyAccessible(url: string) {
  const isLocalhost = localhostPrefixes.some(prefix => url.startsWith(prefix));

  if (isLocalhost) {
    return false;
  }
  return hasHttpUrlScheme(url);
}

/**
 * Returns an image URL width cloudinary transforms to perform
 */
export function convertImageFileWithCloudinary(
  url: string,
  settings: ImageQualityOptions = IMAGE_QUALITY_OPTIONS.medium
) {
  if (!url) return '';

  // Check if running against localhost to avoid Cloudinary params that local backend doesn't understand
  if (isLocalhost()) {
    // For localhost, return clean URLs without Cloudinary-specific query parameters
    if (isPubliclyAccessible(url)) {
      return url;
    }
    // If it's just an ID or path, construct the URL (do not prepend origin to full http(s) URLs)
    if (hasHttpUrlScheme(url)) {
      return url;
    }
    return `${window.location.origin}${url}`;
  }

  // In production, use Cloudinary
  const cld = new Cloudinary({
    cloud: {
      cloudName: 'atellio-development'
    }
  });

  const image = cld.image(url).setDeliveryType('fetch');
  const withOptionalHeight = (api, height?) =>
    height === undefined ? api : api.height(height);

  return withOptionalHeight(
    image
      .format(settings.format || 'auto')
      .quality(settings.quality || 'auto')
      .resize(limitFit(settings.width || '400', settings.height))
      .toURL()
  );
}

const avatarSettings = {
  bySize: {
    xs: { size: Math.ceil(50 * window.devicePixelRatio) },
    sm: { size: Math.ceil(100 * window.devicePixelRatio) },
    md: { size: Math.ceil(200 * window.devicePixelRatio) },
    lg: { size: Math.ceil(300 * window.devicePixelRatio) },
    xl: { size: Math.ceil(400 * window.devicePixelRatio) }
  }
};

export type AvatarSize = keyof typeof avatarSettings.bySize;

const DEFAULT_AVATAR_TRANSFORMATION = 'c_thumb,g_face,z_0.9';
const DEFAULT_GRID_TRANSFORMATION = 'c_thumb,g_face,z_0.65';

function getTransformation(defaultTransformation: string, storageKey?: string) {
  return (
    (storageKey && localStorage.getItem(storageKey)) ?? defaultTransformation
  );
}

function getCloudinaryFaceThumbnail(
  url: Maybe<string>,
  transformation: string,
  width: number,
  height: number
): string {
  if (maybe.isAbsent(url)) return '';
  if (isLocalhost() && !isPubliclyAccessible(url)) return url;

  const cld = new Cloudinary({
    cloud: { cloudName: 'atellio-development' }
  });
  return cld
    .image(url)
    .setDeliveryType('fetch')
    .addTransformation(`${transformation},w_${width},h_${height}`)
    .toURL();
}

/**
 * Returns an avatar image URL with cloudinary transforms to perform
 * @param {string} url The image URL
 * @param {AvatarSize} size The size of the avatar
 * @return {string} The avatar image URL with cloudinary gravity transforms to perform
 */
export function getAvatarImageURL(url: Maybe<string>, size: AvatarSize) {
  const { size: s } = avatarSettings.bySize[size];
  return getCloudinaryFaceThumbnail(
    url,
    getTransformation(DEFAULT_AVATAR_TRANSFORMATION, 'dev_cld_transformation'),
    s,
    s
  );
}

/**
 * Returns a grid thumbnail image URL with Cloudinary face-detection cropping (4:3).
 * Use for resource list grid view to keep faces visible when images are cropped.
 */
export function getGridThumbnailImageURL(url: Maybe<string>): string {
  const width = Math.ceil(560 * window.devicePixelRatio);
  const height = Math.ceil(420 * window.devicePixelRatio);
  return getCloudinaryFaceThumbnail(
    url,
    getTransformation(
      DEFAULT_GRID_TRANSFORMATION,
      'dev_cld_grid_transformation'
    ),
    width,
    height
  );
}
